import { Form, message, Button, Card, Input, DatePicker, Flex } from 'antd';
import React, { useEffect, useState } from 'react';

import certificationService from '../../services/certification.service';
import { useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-components';
import { FilePdfOutlined, DownloadOutlined } from '@ant-design/icons';
import { downloadPDF } from '../../Helpers/Functions';
import modificationService from '../../services/modification.service';
import { useQuery } from 'react-query';

import { createDate, formatDate } from '../../Helpers/time-utils';

const OpacityGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [required, setRequired] = useState(false);
  const [resultStatus, setResultStatus] = useState('');

  const [form] = Form.useForm();
  let { id } = useParams();

  const {
    data: { certification } = {},
    isFetching,
    refetch,
  } = useQuery(
    ['certifications-opacity', id],
    () => modificationService.getOpacity(id),
    {
      keepPreviousData: false,
      enabled: Number(id) > 0,
      refetchOnWindowFocus: false,
    },
  );

  const evaluateOpacity = async () => {
    const { opacity } = form.getFieldsValue();

    if (opacity === undefined || opacity === null || opacity === '') {
      message.warning('Ingresa la opacidad para evaluar.');
      return null;
    }

    try {
      const res = await certificationService.evaluateOpacity(id, {
        opacity,
      });

      if (!res.success) {
        message.error(res.message);
        return null;
      }

      const result = res.data.result;

      form.setFieldsValue({
        result,
      });

      setResultStatus(result);

      message.success(`Resultado evaluado: ${result}`);

      return result;
    } catch (error) {
      message.error(error.message);
      return null;
    }
  };

  const updateOpacity = async () => {
    let result = form.getFieldValue('result');

    if (!result) {
      result = await evaluateOpacity();
    }

    if (!result) {
      return {
        success: false,
        message: 'Debe evaluar la opacidad antes de guardar.',
      };
    }

    const { date, certificate_number, rpm, temperature, opacity } =
      form.getFieldsValue();

    const res = await certificationService.updateOpacity(id, {
      date,
      certificate_number,
      rpm,
      temperature,
      opacity,
      result,
    });

    return res;
  };

  const finish = async () => {
    try {
      const res = await updateOpacity();

      if (!res.success) {
        message.error(res.message);
        return;
      }

      message.success(res.message);
      refetch();
    } catch (error) {
      message.error(error.message);
    }
  };

  const certificateNow = async () => {
    setRequired(true);

    setTimeout(async () => {
      form
        .validateFields()
        .then(async () => {
          setLoading(true);

          const updateRes = await updateOpacity();

          if (!updateRes.success) {
            setLoading(false);
            message.error(updateRes.message);
            return;
          }

          const res = await certificationService.certificate(id);

          setLoading(false);

          if (!res.success) {
            message.error(res.message);
            return;
          }

          refetch();
          message.success(res.message);
        })
        .catch((err) => {
          setLoading(false);
          message.warning(err?.message || '¡Por favor completa los campos!');
        });
    }, 150);
  };

  const removeModel = () => {};

  useEffect(() => {
    if (certification) {
      const result = certification.opacity?.result ?? '';

      form.setFieldsValue({
        certificate_number: certification.certificate_number ?? 'DG-26-0',
        date: createDate(certification.date) ?? createDate(),

        rpm: certification.opacity?.rpm ?? '',
        temperature: certification.opacity?.temperature ?? '',
        opacity: certification.opacity?.opacity ?? '',
        result,
      });

      setResultStatus(result);
    }
  }, [certification, form]);

  return (
    <React.Fragment>
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title={`Opacidad: ${certification?.plate ?? ''} / ${
          certification?.inspection_number ?? ''
        }`}
        subTitle={formatDate(new Date(), 'LLL')}
        extra={[
          certification?.status === 'finished' && (
            <>
              <Button
                icon={<FilePdfOutlined />}
                key="show"
                onClick={() =>
                  downloadPDF(
                    true,
                    certification?.certificate_number,
                    certification?.pdf_url,
                  )
                }
                loading={loading || isFetching}
              >
                Ver
              </Button>
            </>
          ),

          certification?.status === 'finished' && (
            <Button
              icon={<DownloadOutlined />}
              key="download"
              onClick={() =>
                downloadPDF(
                  false,
                  certification?.certificate_number,
                  certification?.pdf_url,
                )
              }
              loading={loading || isFetching}
            >
              Descargar
            </Button>
          ),

          <Button onClick={removeModel} danger key="cancel">
            Anular
          </Button>,

          <Button key="opacity" loading={loading} onClick={certificateNow}>
            Certificar
          </Button>,

          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Guardar
          </Button>,
        ]}
      />

      <Form
        layout="vertical"
        form={form}
        name="control-hooks"
        initialValues={{
          result: '',
        }}
        onFinish={finish}
      >
        <Flex gap={10} justify="space-between" style={{ width: '100%' }}>
          <div style={{ flex: 1 }}>
            <Card className="cardex" title="Datos del certificado">
              <Form.Item
                style={{ width: 200 }}
                name="certificate_number"
                label="Número de Certificado"
                rules={[
                  {
                    required: true,
                    message: 'Ingresa el numero de certificado',
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  aria-autocomplete="none"
                  size="middle"
                />
              </Form.Item>

              <Form.Item
                style={{ marginTop: 10 }}
                name="date"
                label="Fecha de certificación"
                rules={[{ required: true, message: 'Ingresa una fecha' }]}
              >
                <DatePicker style={{ width: 240 }} size="middle" />
              </Form.Item>
            </Card>

            <Card
              className="cardex"
              title="Prueba de opacidad"
              style={{ marginTop: 15 }}
            >
              <Flex gap={10} wrap="wrap" align="start">
                <Form.Item
                  style={{ width: 180 }}
                  name="temperature"
                  label="T Aceite (°C)"
                  rules={[
                    {
                      required,
                      message: 'Ingresa la temperatura',
                    },
                  ]}
                >
                  <Input type="number" size="middle" />
                </Form.Item>

                <Form.Item
                  style={{ width: 180 }}
                  name="rpm"
                  label="RPM"
                  rules={[
                    {
                      required,
                      message: 'Ingresa las RPM',
                    },
                  ]}
                >
                  <Input type="number" size="middle" />
                </Form.Item>

                <Form.Item
                  style={{ width: 180 }}
                  name="opacity"
                  label="Opacidad"
                  rules={[
                    {
                      required,
                      message: 'Ingresa la opacidad',
                    },
                  ]}
                >
                  <Input type="number" step="0.01" size="middle" />
                </Form.Item>

                <Form.Item
                  style={{ width: 220 }}
                  name="result"
                  label="Resultado"
                >
                  <Input
                    size="middle"
                    readOnly
                    style={{
                      fontWeight: 'bold',
                      color:
                        resultStatus === 'APROBADO'
                          ? 'green'
                          : resultStatus === 'DESAPROBADO'
                            ? 'red'
                            : undefined,
                      borderColor:
                        resultStatus === 'APROBADO'
                          ? 'green'
                          : resultStatus === 'DESAPROBADO'
                            ? 'red'
                            : undefined,
                    }}
                  />
                </Form.Item>

                <div style={{ paddingTop: 30 }}>
                  <Button onClick={evaluateOpacity}>Evaluar</Button>
                </div>
              </Flex>
            </Card>
          </div>
        </Flex>
      </Form>
    </React.Fragment>
  );
};

const OpacityBase = () => <OpacityGenerator />;

export default OpacityBase;
